/**
 * Fuente única de color de CRITERO (Manual de Marca · Edición 1.0).
 *
 * Ningún otro fichero debe contener un literal hexadecimal. El prototipo los
 * repartió por más de veinte componentes y re-tematizar después salió carísimo.
 * Los gráficos y cualquier color calculado en JS leen de aquí; el resto usa las
 * clases de Tailwind, que se generan desde este mismo objeto.
 *
 * El lint de CI (`tools/lint-copy.mjs`) falla si aparece un hex fuera de este
 * fichero y de `tailwind.config.js`.
 *
 * ── Qué cambió y por qué ──────────────────────────────────────────────────
 * Hasta aquí esto era el brandbook de 2025: morado #4F46B8, Playfair Display y
 * fondo lila. El Manual de Marca lo sustituye entero, y no como preferencia
 * estética: dice literalmente «PROHIBIDO · Morados y azules de cualquier tipo».
 * El sitio público ya se había rehecho con esta paleta, así que la aplicación
 * era la única pieza que seguía con la identidad vieja — que es exactamente lo
 * que el cliente vio al entrar: «continúa estando la antigua imagen
 * corporativa».
 *
 * Los valores son los mismos que `apps/sitio/app/tokens/colors.css`, que vienen
 * literales del build de referencia del diseñador. Aquí no se reinterpretan.
 */

export const BRAND = {
  /** Crema. Fondo de página. */
  paper: "#FAF9F6",
  /** Superficie secundaria: pistas de puntuación, paneles hundidos. */
  paper2: "#F0EDE6",
  /** Tinta. Texto, botones primarios y secciones oscuras. */
  ink: "#1A1A1A",
  /** Tarjeta sobre tinta y borde del pie. */
  ink2: "#2D2D2D",
  /** Borde dentro de sección oscura. */
  ink3: "#3D3D3D",
  /** Gris cálido. Texto corrido secundario sobre claro. */
  stone: "#6B6560",
  /** Gris cálido secundario. Texto sobre tinta. */
  stone2: "#9B9590",
  /** Borde de 1 px. */
  line: "#E8E2D8",
  /** Teal. Hover, foco y énfasis. Nunca un titular completo. */
  teal: "#2F4F4F",
  /** Oro. Filetes, enlaces, eyebrows y decoración. Nunca un fondo grande. */
  gold: "#B8976A",
  /** Oro claro: el oro sobre tinta, donde el normal se apaga. */
  goldLight: "#D4BA94",
  /** Terracota. Solo riesgo o dato negativo. */
  terracotta: "#C4705B",
  /** Blanco puro: solo tarjeta sobre crema. */
  white: "#FFFFFF",
} as const;

/**
 * La secuencia de color para listas de dimensiones.
 *
 * El manual fija que «el color pertenece a la dimensión, no a la puntuación», y
 * la ilustra con sus seis dimensiones en este orden exacto: teal, oro,
 * terracota, gris, teal, terracota. Son cuatro colores para seis sitios porque
 * la paleta no admite más — «dos acentos, no más».
 *
 * Es un cambio de significado, no de tono. Antes la barra era verde, ámbar o
 * roja según lo bien que se puntuara, y eso convertía el informe en un boletín
 * de notas. Ahora el color dice de qué se habla y el número dice cómo va.
 *
 * ── Ojo con las claves ────────────────────────────────────────────────────
 * Las seis dimensiones del manual —situación profesional, valor de mercado,
 * alineación profesional, claridad de dirección, competencias técnicas,
 * visibilidad profesional— **no existen en el motor**. Lo que el motor calcula
 * son los seis ejes de la rueda de transición (energía, claridad, confianza,
 * seguridad económica, apoyo del entorno, disponibilidad) y las cinco de
 * movilización de valor. Por eso esto es una secuencia por posición y no un
 * mapa por nombre: asignar colores a nombres que no se emiten sería inventar
 * una correspondencia que nadie ha decidido.
 */
export const DIMENSION_SEQUENCE = [
  BRAND.teal,
  BRAND.gold,
  BRAND.terracotta,
  BRAND.stone,
  BRAND.teal,
  BRAND.terracotta,
] as const;

export function dimensionColor(indice: number): string {
  return DIMENSION_SEQUENCE[indice % DIMENSION_SEQUENCE.length] ?? BRAND.stone;
}

/** Color por producto, para tarjetas de precio y cabeceras de informe. */
export const PRODUCT_COLOR = {
  system: BRAND.teal,
  sprint: BRAND.gold,
  diagnostic: BRAND.ink,
  b2b_retention: BRAND.teal,
  b2b_outplacement: BRAND.gold,
  b2b_education: BRAND.ink,
} as const;

/**
 * Color por banda de diagnóstico.
 *
 * Solo tres, y con el reparto que fija el manual: terracota **solo** para
 * riesgo, teal para lo que va bien, oro para la advertencia intermedia. Verde y
 * ámbar desaparecen porque no están en la paleta.
 *
 * Como antes: la banda habla del marco de la persona, no de la persona.
 */
export const BAND_COLOR = {
  green: BRAND.teal,
  amber: BRAND.gold,
  red: BRAND.terracotta,
} as const;

/** Escala del IPC, de bloqueo crítico a preparación acelerable. */
export const IPC_BAND_COLOR = {
  "Bloqueo crítico": BRAND.terracotta,
  "Preparación frágil": BRAND.terracotta,
  "Preparación latente": BRAND.gold,
  "Preparación operable": BRAND.gold,
  "Preparación alta": BRAND.teal,
  "Preparación acelerable": BRAND.teal,
  "Datos insuficientes": BRAND.stone,
} as const;

/** Las bandas llegan de la API como texto libre: la búsqueda lleva respaldo. */
export function bandColor(band: string): string {
  return (BAND_COLOR as Record<string, string>)[band] ?? BRAND.ink;
}

export function ipcBandColor(band: string): string {
  return (IPC_BAND_COLOR as Record<string, string>)[band] ?? BRAND.ink;
}

export type BrandColor = keyof typeof BRAND;
export type ProductKey = keyof typeof PRODUCT_COLOR;
